import { useState, useEffect, useMemo } from 'react';
import { useSearchHistory } from './useSearchHistory';


export const useSearchSuggestions = () => {
  const { recentSearches, bookmarkedSearches } = useSearchHistory();
  const [suggestions, setSuggestions] = useState({
    keywords: [],
    jobTitles: [],
    locations: []
  });

  useEffect(() => {
    const allSearches = [
      ...(recentSearches?.data || []),
      ...(bookmarkedSearches || [])
    ];

    const keywordSet = new Set();
    const jobTitleSet = new Set();
    const locationMap = new Map();

    allSearches.forEach(search => {
      // Extract keywords
      if (search.title && typeof search.title === 'string' && search.title.trim()) {
        keywordSet.add(search.title.trim());
      }
      
      if (search.query) {
        // Extract keyword from query
        if (search.query.keyword && typeof search.query.keyword === 'string' && search.query.keyword.trim()) {
          keywordSet.add(search.query.keyword.trim());
        }
        
        // Extract job titles
        if (search.query.jobTitle && typeof search.query.jobTitle === 'string' && search.query.jobTitle.trim()) {
          jobTitleSet.add(search.query.jobTitle.trim());
        }
        
        if (search.query.targetJobTitle && typeof search.query.targetJobTitle === 'string' && search.query.targetJobTitle.trim()) {
          jobTitleSet.add(search.query.targetJobTitle.trim());
        }
        
        // Extract locations - store as objects when possible
        if (search.query.location) {
          if (typeof search.query.location === 'string' && search.query.location.trim()) {
            // String location - store as is
            locationMap.set(search.query.location.trim(), search.query.location.trim());
          } else if (typeof search.query.location === 'object') {
            // Store location objects with ID as key for deduplication
            if (search.query.location.locationId && search.query.location.name) {
              // Full location object
              locationMap.set(String(search.query.location.locationId), {
                id: String(search.query.location.locationId),
                name: search.query.location.name.trim()
              });
            } else if (search.query.location.locationId) {
              // Only ID - use ID as name for now
              locationMap.set(String(search.query.location.locationId), {
                id: String(search.query.location.locationId),
                name: `Location ID: ${search.query.location.locationId}`
              });
            } else if (search.query.location.name) {
              // Only name - use name as key
              locationMap.set(search.query.location.name.trim(), search.query.location.name.trim());
            }
          }
        }
      }
    });

    // Convert to arrays and sort alphabetically
    setSuggestions({
      keywords: Array.from(keywordSet).sort(),
      jobTitles: Array.from(jobTitleSet).sort(),
      // For locations, convert Map values to array
      locations: Array.from(locationMap.values()).sort((a, b) => {
        // Sort by name
        const nameA = typeof a === 'object' ? a.name : a;
        const nameB = typeof b === 'object' ? b.name : b;
        return nameA.localeCompare(nameB);
      })
    });
  }, [recentSearches, bookmarkedSearches]);

  return suggestions;
}; 